import { useQuery } from "@tanstack/react-query";
import apiClient from "@/api/apiClient";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { getRouteForNavigation } from "@/utils/routeAccess";
import { Heart, Calendar, Activity, TestTube } from "lucide-react";

import StatsCard from "@/components/dashboard/StatsCard";
import RecentTests from "@/components/dashboard/RecentTests";
import UpcomingAppointments from "@/components/dashboard/UpcomingAppointments";
import TestRequests from "@/components/dashboard/TestRequests";

export default function PatientDashboard() {
  // Get user's blood tests
  const { data: bloodTests, isLoading: bloodTestsLoading } = useQuery({
    queryKey: ["bloodTests"],
    queryFn: () => apiClient.get("/api/blood-tests"),
  });

  // Get user's concierge requests
  const { data: conciergeRequests, isLoading: conciergeLoading } = useQuery({
    queryKey: ["conciergeRequests"],
    queryFn: () => apiClient.get("/api/concierge-testing"),
  });

  // Get user's bookings
  const { data: bookings, isLoading: bookingsLoading } = useQuery({
    queryKey: ["bookings"],
    queryFn: () => apiClient.get("/api/bookings"),
  });

  // Calculate stats
  const upcomingBookings =
    bookings?.filter(
      (booking) =>
        new Date(booking.date) >= new Date() && booking.status === "scheduled"
    ) || [];

  const pendingConciergeCount =
    conciergeRequests?.filter(
      (req) => req.status === "pending" || req.status === "in_progress"
    ).length || 0;

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-8" data-testid="patient-dashboard-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">
            My Health Dashboard
          </h1>
          <p className="text-slate-600">
            Welcome back! Here&apos;s your health overview and recent activity.
          </p>
        </div>
        <div className="flex gap-3">
          <Link to={getRouteForNavigation("Concierge Testing")}>
            <Button className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-lg">
              <TestTube className="w-4 h-4 mr-2" />
              View Test Requests
            </Button>
          </Link>
          <Link to={getRouteForNavigation("Patient Calendar")}>
            <Button
              variant="outline"
              className="border-slate-300 hover:bg-slate-50"
            >
              <Calendar className="w-4 h-4 mr-2" />
              Book Appointment
            </Button>
          </Link>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <StatsCard
          title="Blood Tests"
          value={bloodTests?.length || 0}
          icon={Heart}
          color="red"
        />

        <StatsCard
          title="Appointments"
          value={upcomingBookings.length}
          subtitle="upcoming"
          icon={Calendar}
          color="green"
        />

        <StatsCard
          title="Test Requests"
          value={conciergeRequests?.length || 0}
          subtitle={
            pendingConciergeCount > 0
              ? `${pendingConciergeCount} pending`
              : undefined
          }
          icon={Activity}
          color="purple"
        />
      </div>

      {/* Upcoming Appointments */}
      <UpcomingAppointments
        appointments={bookings}
        isLoading={bookingsLoading}
        viewAllUrl="Patient Calendar"
        isPatientView={true}
      />

      {/* Main Content Grid */}
      <div className="grid lg:grid-cols-2 gap-8">
        {/* Recent Blood Tests */}
        <RecentTests tests={bloodTests || []} isLoading={bloodTestsLoading} />

        {/* Recent Concierge Requests */}
        <TestRequests
          conciergeRequests={conciergeRequests}
          isLoading={conciergeLoading}
          title="Test Requests"
          emptyStateButtonText="Request Your First Test"
        />
      </div>
    </div>
  );
}
